import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/lib/usePageTitle";
import { Sparkles, ShieldCheck, KeyRound } from "lucide-react";
import { TRADES } from "@/lib/trades";

const ART = { house: "/brand/house.webp", check: "/brand/check.webp", trophy: "/brand/trophy.webp" };

const Sprig = () => (
  <div className="sprig">
    <span className="line" />
    <svg width="26" height="18" viewBox="0 0 26 18" fill="none" stroke="#C97C8C" strokeWidth="1.1"><path d="M2 16C10 12 16 6 24 2" /><path d="M8 12 6 8M13 8l-1-4M18 5l-1-4" strokeLinecap="round" /></svg>
    <span className="line" />
  </div>
);

export default function Landing() {
  usePageTitle("Olivia Does — One name your home can trust");

  useEffect(() => {
    const ld = {
      "@context": "https://schema.org", "@type": "Organization", "name": "Olivia Does",
      "url": window.location.origin, "description": "A curated home concierge for UK homes — one trusted provider for every residential property need.",
      "areaServed": { "@type": "Country", "name": "United Kingdom" }, "slogan": "One name your home can trust.",
    };
    const id = "od-jsonld-org"; let el = document.getElementById(id);
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
    el.textContent = JSON.stringify(ld);
    return () => { el && el.remove(); };
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="od-wrap hero">
        <div className="fade-in">
          <div className="od-eyebrow" data-testid="hero-tagline">A curated home concierge</div>
          <h1 data-testid="hero-headline">One name your home can trust.<br /><i>Olivia Does</i> the rest.</h1>
          <p className="lead">Tell us what your home needs, in your own words. We scope it, match it to one of our hand-picked partner trades — or handle it ourselves — and stay on it until it's done properly.</p>
          <div className="heroctas">
            <Link to="/book" data-testid="hero-cta-book" className="btn primary">Describe your job →</Link>
            <Link to="/services" data-testid="hero-cta-services" className="btn secondary">Browse services</Link>
          </div>
          <div className="trustrow">
            <span><ShieldCheck className="icon" style={{ width: 18, height: 18 }} />4-stage vetted partners</span>
            <span><Sparkles className="icon" style={{ width: 18, height: 18 }} />Fixed prices on everyday jobs</span>
            <span><KeyRound className="icon" style={{ width: 18, height: 18 }} />Payment protected until it's done</span>
          </div>
        </div>
        <div className="fade-in">
          <div className="heropanel">
            <img className="brandart" src={ART.house} alt="An Olivia Does home" />
            <div className="floatcard">
              <div className="dot" />
              <div><b>Live booking</b><span>Leaking tap · matched to a partner in 8 minutes</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* TRADES STRIP */}
      <section className="od-section od-wrap" data-testid="home-trades-strip">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 30, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="od-eyebrow">Our trades</div>
            <h2 className="serif" style={{ fontSize: "2rem", fontWeight: 500, margin: 0 }}>Six trades, one standard.</h2>
          </div>
          <Link to="/trades" data-testid="home-trades-link" className="od-link">See what each covers →</Link>
        </div>
        <div className="tradestrip">
          {TRADES.map(({ slug, name, Icon }) => (
            <Link to={`/trades#${slug}`} key={slug} data-testid={`home-trade-${slug}`} className="tradestrip-item">
              <Icon className="icon" aria-hidden="true" />
              <span>{name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="od-section-dark">
        <div className="od-wrap od-section">
          <div className="od-eyebrow">How it works</div>
          <h2 className="serif" style={{ fontSize: "2.1rem", fontWeight: 400, margin: 0, maxWidth: 520 }}>Designed to feel effortless.</h2>
          <div className="steps">
            {[["01", "Describe", "Tell Olivia what's going on, in plain English. Our concierge sorts the trade and the urgency."],
              ["02", "We match", "We scope the job properly, then hand it to one of our vetted partners — or handle it ourselves."],
              ["03", "Job done", "One quote, one point of contact, one standard — and we don't step back until it's right."]].map(([n, t, d]) => (
              <div key={n} data-testid={`how-step-${n}`}><div className="num">{n}</div><h3>{t}</h3><p>{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STANDARD */}
      <section className="od-section od-wrap">
        <div className="panelrow">
          <div className="heropanel" style={{ minHeight: 300 }}>
            <img className="brandart" src={ART.check} alt="Held to a higher mark" />
          </div>
          <div>
            <div className="od-eyebrow">Our standard</div>
            <h2 className="serif" style={{ fontSize: "2rem", fontWeight: 500, margin: "0 0 16px" }}>Held to a higher mark.</h2>
            <p style={{ color: "var(--taupe)", lineHeight: 1.7, marginBottom: 22 }}>A home isn't a job site — it's where someone keeps what they actually care about. So our vetting isn't a box ticked once; it's a standard we hold every partner to, every time.</p>
            <div className="ogrid cols-2" style={{ gap: 14 }}>
              <div className="statcard"><b>4-stage vetting</b><span>On every partner trade</span></div>
              <div className="statcard"><b>Payment protected</b><span>Held until you approve the work</span></div>
              <div className="statcard"><b>Live status</b><span>On every single booking</span></div>
              <div className="statcard"><b>Aftercare promise</b><span>If it's not right, we make it right</span></div>
            </div>
          </div>
        </div>
      </section>

      <Sprig />

      {/* PARTNER CTA */}
      <section className="od-section od-wrap">
        <div className="panelrow">
          <div>
            <div className="od-eyebrow">Partner network</div>
            <h2 className="serif" style={{ fontSize: "2rem", fontWeight: 500, margin: "0 0 16px" }}>Are you a trade we'd be proud of?</h2>
            <p style={{ color: "var(--taupe)", lineHeight: 1.7, marginBottom: 24 }}>Our in-house team handles general property care directly. When a job calls for a specific trade, that's where our partner panel comes in — a small, personalised group, not one of thirty names on a directory.</p>
            <Link to="/partners" data-testid="partner-cta" className="btn primary">Apply to join →</Link>
          </div>
          <div className="heropanel" style={{ minHeight: 260 }}>
            <img className="brandart" src={ART.trophy} alt="A trade we'd be proud of" />
          </div>
        </div>
      </section>

      {/* FAQ strip */}
      <section className="od-wrap" style={{ paddingBottom: 88 }} data-testid="faq-link-section">
        <div className="card" style={{ background: "var(--charcoal)", color: "var(--cream)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, padding: "34px 40px", border: 0 }}>
          <div>
            <h2 className="serif" style={{ fontSize: "1.7rem", margin: 0 }}>Got questions?</h2>
            <p style={{ color: "#CFC6BF", marginTop: 6 }}>Booking, vetting, pricing and our aftercare promise — all answered.</p>
          </div>
          <Link to="/faqs" data-testid="faq-link-cta" className="btn rose">Read our FAQs →</Link>
        </div>
      </section>
    </div>
  );
}
